import { useUnmount } from 'ahooks';
import React, { useEffect, useState } from 'react';
import { type Editor } from '@meitu/saas-editor-core';
import { type FontItem } from '@/components/font-family-selector';
import { EditorContext } from '@/hooks/use-editor';
import { type ListStyleStruct } from '@/types';
import { VectorPanelContext } from './context';
import { VectorImagePanel, VectorTextPanel } from './panels';
import { type PanelTab } from './types';
import { VectorPanelHeader } from './widgets';
import './style.less';
import './shared.less';
import { TextEditPlugin } from '@meitu/saas-editor-core/es/plugins/text-edit';

type Props = {
    isIntl: boolean;
    fonts: FontItem[];
    editor: Editor;
    layerId: string;
    /**
     * 列表样式列表
     */
    listStyles: ListStyleStruct[];
    /**
     * 是否是 VIP 用户
     */
    isVIP?: boolean;
    /**
     * 打开 VIP 弹窗
     */
    onOpenVIPModal?: () => void;
    /**
     * 应用层作图记录 id，主要用于埋点
     */
    recordId?: number;
    /**
     * 是否启用远程搜索
     */
    enableRemoteSearch?: boolean;
};

export const VectorLayerPanel: React.FC<Props> = ({
    isIntl,
    fonts,
    editor,
    layerId,
    listStyles,
    recordId,
    isVIP,
    onOpenVIPModal,
    enableRemoteSearch,
}) => {
    const [currentTab, setCurrentTab] = useState<PanelTab>('image');

    const handleSwitchTab = (tab: PanelTab) => {
        if (tab === currentTab) return;

        if (tab === 'image') {
            const editingLayer = editor.getInstance(TextEditPlugin).getEditingLayer();
            if (editingLayer) editor.getInstance(TextEditPlugin).exitEditing();
        }

        setCurrentTab(tab);
    };

    // HACK: 文字编辑层的时候有在 mousedown 事件阻止冒泡
    // 但是阻止冒泡之后，点击文字编辑框就会影响弹出层自动收起
    // 后尝试在进入编辑态时调用 disableMouseSelect，退出编辑时调用 enableMouseSelect 发现不生效（disableMouseSelect 后仍可以拉动选框）
    // 郑鹏反馈这个问题不太好改，暂时先在业务方进行 HACK
    useEffect(() => {
        const dispose = editor.getInstance(TextEditPlugin).onMouseDown(() => {
            const body = document.body;

            if (body) {
                const clickEvent = new MouseEvent('mousedown', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                });

                body.dispatchEvent(clickEvent);
            }
        });
        return () => dispose();
    }, []);

    // 切换图层时重置 tab
    useEffect(() => {
        setCurrentTab('image');
    }, [layerId]);

    useUnmount(() => {
        const editingLayer = editor.getInstance(TextEditPlugin).getEditingLayer();
        if (editingLayer && editingLayer.id === layerId) editor.getInstance(TextEditPlugin).exitEditing();
    });

    return (
        <EditorContext.Provider value={editor}>
            <VectorPanelContext.Provider value={{ recordId, layerId, isIntl, isVIP, onOpenVIPModal, listStyles }}>
                <div className="vector-layer-panel">
                    <VectorPanelHeader
                        currentTab={currentTab}
                        onSwitchTab={handleSwitchTab}
                        layerId={layerId}
                        recordId={recordId}
                    />

                    <div className="vector-layer-panel__content">
                        {currentTab === 'image' ? (
                            <VectorImagePanel />
                        ) : (
                            <VectorTextPanel fonts={fonts} enableRemoteSearch={enableRemoteSearch} />
                        )}
                    </div>
                </div>
            </VectorPanelContext.Provider>
        </EditorContext.Provider>
    );
};
